import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {
  ChartCard,
  ReportDonutChart,
  ReportHorizontalBarChart,
  ReportMultiSeriesChart,
  CHART_COLOURS
} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {monthYear} from './components/labels';
import {findReportBySlug} from './reportCatalogue';

/**
 * REV4 — Student Subject Orders.
 *
 * Monthly per-subject student-count billing. Same anatomy and shared components
 * as Materials Revenue; only the query and the columns differ.
 *
 * No region filter, for the same reason as every money report: geography lives
 * on postcodes and does not map cleanly to a franchise.
 */

const SLUG = 'student-subject-orders';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const money = (v: any) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(Number(v) || 0);

const StudentSubjectOrders = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Student Subject Orders');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const byPeriod = collections?.series?.by_period || [];
  const bySubject = collections?.series?.by_subject || [];
  const byPayment = collections?.series?.by_payment || [];
  const detail = collections?.detail;

  const columns = [
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {name: 'Subject', selector: (row: any) => row.subject || '---', width: '130px'},
    {name: 'Period', selector: (row: any) => monthYear(row.period) || '---', width: '140px'},
    {
      name: 'Students',
      width: '100px',
      right: true,
      selector: (row: any) => row.students
    },
    {
      name: 'Rate',
      width: '100px',
      right: true,
      selector: (row: any) => money(row.rate)
    },
    {
      name: 'VAT',
      width: '100px',
      right: true,
      selector: (row: any) => money(row.vat)
    },
    {
      name: 'Total',
      width: '120px',
      right: true,
      selector: (row: any) => money(row.total_price)
    },
    {
      name: 'Payment',
      width: '110px',
      cell: (row: any) => (
        <span
          className={`badge badge-${
            row.payment === 'Paid' ? 'success'
              : row.payment === 'Unpaid' ? 'warning' : 'secondary'
          }`}>
          {row.payment}
        </span>
      )
    }
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Student Subject Orders'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Student Subject Orders</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, subject: true, paymentStatus: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-lg-8">
              <ChartCard
                title="Billed value and participating franchises"
                subtitle="Bars are billed value; the line is how many franchises billed that period"
                loading={loading}
                isEmpty={!byPeriod.length}>
                <ReportMultiSeriesChart
                  data={byPeriod}
                  xKey="bucket"
                  series={[
                    {key: 'value', name: 'Billed value', type: 'bar', colour: CHART_COLOURS[3]},
                    {key: 'franchises', name: 'Franchises', type: 'line', axis: 'right', colour: CHART_COLOURS[2]}
                  ]}
                />
              </ChartCard>
            </div>
            <div className="col-lg-4">
              <ChartCard
                title="Paid vs unpaid"
                subtitle="Billed value by payment status"
                loading={loading}
                isEmpty={!byPayment.length}>
                <ReportDonutChart data={byPayment} nameKey="status" valueKey="value" />
              </ChartCard>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <ChartCard
                title="Value by subject"
                subtitle="Billed value across subjects"
                loading={loading}
                isEmpty={!bySubject.length}
                height={220}>
                <ReportHorizontalBarChart
                  data={bySubject}
                  labelKey="subject"
                  valueKey="value"
                  height={180}
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">Billing rows</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 25}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">
                    No billing rows match these filters.
                  </div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudentSubjectOrders;
